import { useState } from 'react'
import { motion } from 'framer-motion'
import { KeyIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { authApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

/**
 * Blocking modal shown when the logged-in account still has a temporary
 * password (login returned must_change_password=true) — e.g. a freshly
 * provisioned client admin or an invited user. They cannot use the app until
 * they set a new password.
 */
export default function ForcedPasswordChange() {
  const { setMustChangePassword, logout } = useAuth()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)

  const tooShort = next.length > 0 && next.length < 8
  const mismatch = confirm.length > 0 && next !== confirm
  const canSubmit = current && next.length >= 8 && next === confirm && !saving

  const submit = async (e) => {
    e.preventDefault()
    if (!canSubmit) return
    setSaving(true)
    try {
      await authApi.changePassword({ current_password: current, new_password: next })
      toast.success('Password updated')
      setMustChangePassword(false)
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change password')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="glass-card w-full max-w-md rounded-2xl border border-cyan-500/20 p-6"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-cyan-500/15 ring-1 ring-cyan-500/30">
            <KeyIcon className="h-6 w-6 text-cyan-400" />
          </div>
          <h2 className="text-xl font-semibold text-white">Set a new password</h2>
        </div>
        <p className="text-sm text-slate-400 mb-5">
          Your account was created with a temporary password. Choose a new one to continue.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Temporary password</label>
            <input
              type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
              autoFocus
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">New password</label>
            <input
              type="password" value={next} onChange={(e) => setNext(e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            <p className={`mt-1 text-xs ${tooShort ? 'text-amber-400' : 'text-slate-500'}`}>
              At least 8 characters.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Confirm new password</label>
            <input
              type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            {mismatch && <p className="mt-1 text-xs text-amber-400">Passwords don't match.</p>}
          </div>
          <div className="flex items-center justify-between gap-3 pt-2">
            <button
              type="button" onClick={logout}
              className="px-4 py-2 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
            >
              Sign out
            </button>
            <button
              type="submit" disabled={!canSubmit}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-cyan-500/20 text-cyan-400 ring-1 ring-cyan-500/40 hover:bg-cyan-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? 'Updating…' : 'Set password'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}
